import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import ToastDefault from "../../DefaultComponent/Toast";
import { calculateTotalPages } from "../../../utils/Helper";
import {
  TopupPresetListAction,
  TopupPresetCreateAction,
  TopupPresetUpdateAction,
  TopupPresetDeleteAction,
} from "../../../Redux/Actions/PricingAction";
import {
  PricingPageHeader,
  StatusChip,
  pricingBodyRowSx,
  pricingTableContainerSx,
  pricingTableHeadSx,
} from "../shared/pricingHelpers";
import TopupPresetsForm, {
  TopupPresetValues,
} from "./TopupPresetsForm/TopupPresetsForm";

const ROWS_PER_PAGE = 10;

const TopupPresets: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { rows = [], loading } = useSelector(
    (state: any) => state.TopupPresetListData || {}
  );

  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchList = () => dispatch(TopupPresetListAction());

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paged = useMemo(
    () => rows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE),
    [rows, page]
  );

  const openCreate = () => {
    setMode("create");
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: any) => {
    setMode("edit");
    setEditing(row);
    setFormOpen(true);
  };

  const initialFromRow = (row: any): Partial<TopupPresetValues> | null =>
    row
      ? {
          amount: row.amount ?? "",
          currency: row.currency ?? "USD",
          label: row.label ?? "",
          display_order: row.display_order ?? 0,
          is_active: row.is_active ?? true,
        }
      : null;

  const handleSubmit = async (payload: any) => {
    setSaving(true);
    const res =
      mode === "create"
        ? await dispatch(TopupPresetCreateAction(payload))
        : await dispatch(TopupPresetUpdateAction(editing.id, payload));
    setSaving(false);
    if (res?.status === true) {
      setFormOpen(false);
      fetchList();
    }
  };

  const handleDelete = (row: any) => {
    Swal.fire({
      title: "Delete preset?",
      text: `Top-up preset of ${row.amount} ${row.currency} will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d92d20",
      cancelButtonColor: "#667085",
      confirmButtonText: "Delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const res = await dispatch(TopupPresetDeleteAction(row.id));
        if (res?.status === true) fetchList();
      }
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Top-up Presets"
            breadcrumbs={[
              { label: "Pricing", path: "/pricing/topup-presets", isActive: false },
              { label: "Top-up Presets", path: "/pricing/topup-presets", isActive: true },
            ]}
          />
        </div>

        <PricingPageHeader
          title="Top-up Presets"
          subtitle="Quick top-up tiles shown on the buy-credits screen."
          actionLabel="Add Preset"
          onAction={openCreate}
        />

        <Box sx={{ mx: 2, mb: 2 }}>
          <TableContainer component={Paper} sx={pricingTableContainerSx}>
            <Table aria-label="Top-up Presets">
              <TableHead sx={pricingTableHeadSx}>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell align="right">Order</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length > 0 ? (
                  paged.map((row: any, idx: number) => (
                    <TableRow key={row.id} sx={pricingBodyRowSx}>
                      <TableCell>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                      <TableCell align="right">{row.amount}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell>{row.label ?? "—"}</TableCell>
                      <TableCell align="right">{row.display_order}</TableCell>
                      <TableCell align="center">
                        <StatusChip active={Boolean(row.is_active)} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row)}
                            sx={{ color: "#d92d20" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: "#667085" }}>
                      No top-up presets found. Click “Add Preset” to create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {rows.length > ROWS_PER_PAGE && (
          <div className="pagination-style">
            <Stack spacing={2}>
              <Pagination
                count={calculateTotalPages(rows.length, ROWS_PER_PAGE)}
                page={page}
                onChange={(_e, p) => setPage(p)}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Stack>
          </div>
        )}
      </div>

      <TopupPresetsForm
        open={formOpen}
        mode={mode}
        initial={initialFromRow(editing)}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />
      <ToastDefault />
    </>
  );
};

export default TopupPresets;
